import { h, Component } from "preact";
import cx from "classnames";
import { Subscription, Subject } from "rxjs";
import moment from "moment-timezone";
import { map, filter, distinctUntilChanged } from "rxjs/operators";

import { ModalProps, Modal } from "../../common/modal";
import { Form, FieldList, Field } from "../../../../../types/form.model";
import * as Validators from "../../../../../types/validator.model";
import {
  validateForm,
  cxValidateField,
} from "../../../../../types/validator.model";
import linkform, { updateFormFieldValue } from "../../../../utils/linkform";
import getStore, { appState$ } from "../../../store";

import {
  PurchaseDocument,
  ReceivingReport,
} from "../../../../../types/purchase-document.model";
import {
  REQUEST_IN_PROGRESS,
  REQUEST_SUCCESS,
  REQUEST_UNTOUCHED,
} from "../../../../../types/request-state.model";
import { Item, ItemPiece } from "../../../../../types/item.model";
import { ItemSearch } from "../../common/item-search.component";
import { toFixed } from "../../../../utils/decimal";
import { getSpinnerBlock } from "../../common/spinner-block";
import {
  CREATE_IW,
  CREATE_PURCHASE_DOCUMENT,
  CREATE_RECEIVING_REPORT,
  UPDATE_PURCHASE_DOCUMENT,
} from "../../../reducers/motorpool/motorpool.actions";
import { DatePicker } from "preact/components/common/date-picker";
import { ItemWithdrawal } from "../../../../../types/item-withdrawal.model";

interface CreateIWProps extends ModalProps {}

interface CreateIWState {
  form?: Form;
  itemList: Item[];
  isPostInProgress?: boolean;
}

export class CreateIWModal extends Modal<CreateIWProps, CreateIWState> {
  form$: Subject<Form>;
  subs: Subscription[] = [];

  constructor(props: CreateIWProps) {
    super(props);

    this.createForm(props);
    this.setState({
      itemList: [],
    });
  }

  createForm(props: CreateIWProps) {
    let fields: FieldList = {
      requestedBy: new Field([Validators.required], null, null),
      reason: new Field([Validators.required], null, null),
    };

    this.form$ = new Subject<Form>();
    this.setState({
      form: new Form(fields),
    });

    this.form$.subscribe((form) => {
      validateForm(form);

      let state = {};
      state["form"] = form;
      this.setState(state);
    });
  }

  componentDidMount(): void {
    super.componentDidMount();

    const motorpool$ = appState$.pipe(map((appState) => appState.motorpool));

    this.subs.push(
      motorpool$
        .pipe(
          map((motorpool) => motorpool.postPutRequestState),
          distinctUntilChanged()
        )
        .subscribe((postPutRequestState) => {
          if (postPutRequestState === REQUEST_SUCCESS) {
            this.setState({
              isPostInProgress: false,
            });
            this.props.close();
          } else if (postPutRequestState === REQUEST_IN_PROGRESS) {
            this.setState({
              isPostInProgress: true,
            });
          }
        })
    );
  }

  doModalClose = () => {
    if (this.props.close) {
      this.props.close();
    }
  };

  onSubmit = () => {
    const form = this.state.form;
    validateForm(form);

    if (!form.isValid) {
      return;
    }

    let iw: ItemWithdrawal = {
      requestedBy: form.fields["requestedBy"].value,
      reason: form.fields["reason"].value,
      itemCodes: this.state.itemList,
    };

    const store = getStore();
    store.dispatch({
      type: CREATE_IW,
      payload: {
        iwToCreate: iw,
      },
    });
  };

  onItemSelected = (a: Item) => {
    let itemList = this.state.itemList;
    const existing = itemList.find((member) => member.itemCode === a.itemCode);
    a.amountStock = a.amount;

    if (!existing) {
      // brute force setting amount to 1 (we don't set the amount in system)
      a.amount = 1;
      itemList.push(a);

      this.setState({
        itemList: itemList,
      });
    }
  };

  onDateSelected = (date: string, fieldName: string) => {
    updateFormFieldValue(this.form$, this.state.form, fieldName, date);
  };

  getFormBlock(props: CreateIWProps, state: CreateIWState) {
    const form = this.state.form;

    if (!form) {
      return;
    }

    return (
      <div className="row">
        <div className="col-12">
          <div className="row">
            <div class="col-12 col-lg-6 mt-3">
              <label class="anglo-remittance__submit-form__label">
                Requested By*
              </label>
              <input
                className={cx("form-control", {
                  invalid: cxValidateField(form.fields["requestedBy"]),
                })}
                type="text"
                value={form.fields["requestedBy"].value}
                onChange={linkform(this.form$, form, "requestedBy")}
                placeholder="Requested By"
              />
            </div>
            <div class="col-12 col-lg-6 mt-3">
              <label class="anglo-remittance__submit-form__label">
                Reason*
              </label>
              <input
                className={cx("form-control", {
                  invalid: cxValidateField(form.fields["reason"]),
                })}
                type="text"
                value={form.fields["reason"].value}
                onChange={linkform(this.form$, form, "reason")}
                placeholder="Reason"
              />
            </div>
          </div>
          <hr />
          <p class="m-0">
            <b>Items to be Withdrawn</b>
          </p>
          <div className="row">
            <div class="col-12 mt-3">
              <ItemSearch onResultSelected={this.onItemSelected} />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              {state.itemList.length > 0
                ? this.getItemList(state.itemList)
                : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  removeItem = (index: number) => {
    let itemList = this.state.itemList;
    itemList.splice(index, 1);
    this.setState({
      itemList: itemList,
    });
  };

  onItemAmountChange = (event, index: number) => {
    const target = event.target;
    const value = target.value;
    let itemList = this.state.itemList;
    itemList[index].amount = value;

    this.setState({
      itemList: itemList,
    });
  };

  getItemList(itemList: Item[]) {
    return (
      <div class="item-list data-grid">
        <div className="data-grid__row header">
          <div class="data-grid__col">Name</div>
          <div class="data-grid__col">Item Code</div>
          <div class="data-grid__col">Supplier</div>
          <div class="data-grid__col col-short">Unit Price</div>
          <div class="data-grid__col col-short">Available In Stock</div>
          <div class="data-grid__col col-short">Amount</div>
          <div class="data-grid__col">Total</div>
          <div class="data-grid__col col-short">Remove</div>
        </div>
        {itemList.map((row, index) => {
          return (
            <div className="data-grid__row">
              <div class="data-grid__col">{row.name}</div>
              <div class="data-grid__col">{row.itemCode}</div>
              <div class="data-grid__col">{row.supplierId}</div>
              <div class="data-grid__col col-short">
                ₱ {toFixed(row.unitPrice)}
              </div>
              <div class="data-grid__col col-short">{row.amountStock}</div>
              <div class="data-grid__col col-short">
                <input
                  className={"form-control"}
                  type="number"
                  value={row.amount}
                  onChange={(ev) => this.onItemAmountChange(ev, index)}
                  placeholder="Requested By"
                />
              </div>
              <div class="data-grid__col">
                ₱ {toFixed(row.unitPrice * row.amount)}
              </div>
              <div class="data-grid__col col-short">
                <button
                  class="btn btn-outline-dark"
                  onClick={(ev) => this.removeItem(index)}
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  getActionsRow(props: CreateIWProps, state: CreateIWState) {
    if (state.isPostInProgress) {
      return getSpinnerBlock(state.isPostInProgress);
    } else {
      return (
        <div class="col-12">
          <div class="row">
            <div class="col-12 col-lg-3 offset-lg-9">
              <button
                class="btn btn-primary w-100"
                onClick={this.onSubmit}
                disabled={!state.form.isValid}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  render(props: CreateIWProps, state: CreateIWState) {
    return (
      <div
        class="container anglo-modal create-pd-modal"
        ref={(node) => (this.refNode = node)}
      >
        <div class="row anglo-modal__head align-items-center">
          <div class="col-9 col-md-10">
            <h3 class="mb-0">Create Item Withdrawal</h3>
          </div>
          <div class="col-3 col-md-2">
            <button
              class="btn btn-outline-light w-100"
              onClick={this.doModalClose}
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="row anglo-modal__body py-3">
          <div class="col-12 create-withdrawal-modal__body pb-3">
            {this.getFormBlock(props, state)}
          </div>
        </div>

        <div class="row vehicle-card__actions">
          {this.getActionsRow(props, state)}
        </div>
      </div>
    );
  }
}
